/**
 * Sprint 11 Phase 2 — Suppression-bypass audit viewer.
 *
 * Lives at /dashboard/admin/audit/suppression-bypass (matching the
 * Sprint 9 Individual Deployment spec). Read-only paged view of every
 * `kind: suppression_bypass` audit row — who tried to read which
 * client, from where, when, and whether they were allowed.
 *
 * Super-admin only (gated at the route + the API endpoint).
 *
 * The page accepts an optional `?deployment_id=<uuid>` query param
 * which scopes the viewer to one deployment — used by the
 * "Audit log →" link on the Deployment Detail header for individual
 * mode.
 */
import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function SuppressionBypassAudit() {
  const { logout } = useAuth();
  const [searchParams] = useSearchParams();
  const scopedDeploymentId = searchParams.get("deployment_id") || null;

  const [rows, setRows] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = { limit: 100 };
        if (scopedDeploymentId) params.deployment_id = scopedDeploymentId;
        const { data } = await api.get("/api/admin/audit/suppression-bypass", { params });
        setRows(data.rows || []);
        setTotal(data.total || 0);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [scopedDeploymentId]);

  return (
    <div className="min-h-screen bg-paper" data-testid="suppression-bypass-audit-page">
      <header className="border-b border-gray-100 sticky top-0 z-10 bg-paper">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="wordmark text-sm">
              <span style={{ color: "#1A1A1A" }}>RESILIENT</span>{" "}
              <span style={{ color: "#2F6B7C" }}>TEAMS</span>
            </span>
            <span
              className="eyebrow"
              style={{ color: "#6F6F6F" }}
            >
              · Audit · Suppression bypass
            </span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/dashboard/admin/cleanup" className="btn-quiet" style={{ textDecoration: "none" }} data-testid="back-to-cleanup">
              ← Admin tools
            </Link>
            <Link to="/dashboard" className="btn-quiet" style={{ textDecoration: "none" }}>
              Home
            </Link>
            <button onClick={logout} className="btn-quiet" data-testid="logout-button">
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-8 py-12">
        <h1 className="text-2xl mb-2" style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A" }}>
          Suppression-bypass audit
        </h1>
        <p className="text-sm text-gray-600 mb-2 max-w-prose">
          Every time a consultant reads a single client&rsquo;s data on an individual deployment, downloads an ORI
          member&rsquo;s report, or reveals an ORI subgroup with fewer than five completions, a row is recorded here —
          whether the read was allowed (assigned coach or super-admin) or denied (anyone else). Use this to review
          who has been looking at whose engagements.
        </p>
        {scopedDeploymentId && (
          <p className="text-xs mb-6" style={{ color: "#2563EB" }} data-testid="scope-indicator">
            Filtered to deployment <span style={{ fontFamily: "monospace" }}>{scopedDeploymentId}</span> ·{" "}
            <Link to="/dashboard/admin/audit/suppression-bypass" className="btn-quiet" style={{ textDecoration: "underline" }}>
              show all
            </Link>
          </p>
        )}

        {error && <div className="text-sm" style={{ color: "#AD393E" }}>{error}</div>}
        {loading && <div className="text-sm text-gray-500">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="text-sm text-gray-500 mt-6" data-testid="audit-empty-state">
            No bypass attempts recorded {scopedDeploymentId ? "for this deployment" : "yet"}.
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead>
                <tr style={{ background: "#FBFAF7" }}>
                  {["When", "User", "Query type", "Path", "Outcome", "From"].map((h) => (
                    <th key={h} className="text-left py-2 px-3" style={{ fontFamily: "Oswald", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase", color: "#6F6F6F" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id || r.created_at} style={{ borderBottom: "1px solid #EEE" }} data-testid="audit-row">
                    <td className="py-2 px-3 text-xs text-gray-700">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="py-2 px-3">{r.user_email} <span className="text-xs text-gray-500">({r.user_role})</span></td>
                    <td className="py-2 px-3 text-xs">{r.query_type}</td>
                    <td className="py-2 px-3 text-xs text-gray-700" style={{ fontFamily: "monospace" }}>
                      {r.request_path}
                      {r.filters_used && Object.keys(r.filters_used).length > 0 && (
                        <div className="text-gray-500" data-testid="audit-row-filters">
                          {Object.entries(r.filters_used).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`).join(" · ")}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <span style={{
                        fontFamily: "Oswald", fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase",
                        padding: "2px 8px",
                        background: r.outcome === "allowed" ? "#E2F3EC" : "#FCE7E8",
                        color: r.outcome === "allowed" ? "#1B8A6B" : "#AD393E",
                      }}>
                        {r.outcome}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-xs text-gray-600">{r.ip_address || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-xs text-gray-500 mt-3">
              Showing {rows.length} of {total} total · super-admin only
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
